'use client';

import { Accordion, AppImage } from '@workspace/ui/common';
import { Typography } from '@workspace/ui/common';
import { sanityImageSrc } from '@/sanity/lib/image';
import { cn } from '@workspace/lib';
import { type HeadingType } from '@workspace/lib/heading-utils';
import { getBackgroundClass } from '@workspace/lib/helper';
import { useDocumentBlock } from './useDocumentBlock';
import { SanityDocumentSection } from './document-block.types';
import { SanityImage } from '@workspace/interfaces';

interface DocumentBlockProps {
  block: {
    title?: HeadingType;
    image?: SanityImage;
    customBackgroundColor?: string | { hex: string };
    backgroundColor?: string;
    documentSections: SanityDocumentSection[];
    defaultExpanded?: string[];
    imagePosition?: 'left' | 'right' | 'hidden';
    isEnabled?: boolean;
  };
  index?: number;
}

export function DocumentBlock({ block }: DocumentBlockProps) {
  const {
    resolvedTitleText,
    cleanImagePosition,
    transformedAccordionItems,
    defaultExpandedIds,
    shouldRender,
    hasVisibleSections,
  } = useDocumentBlock(block);

  // Early return if component should not render
  if (!shouldRender) {
    return null;
  }

  const backgroundClass = getBackgroundClass(block.backgroundColor || block.customBackgroundColor);

  return (
    <div className={cn('document-block-bg w-full', backgroundClass)}>
      <section className="py-xl md:py-3xl px-md md:px-md-banner porta-container">
        <div className="porta-container p-0!">
          {resolvedTitleText && (
            <div className="text-center mb-12">
              <Typography variant="h2" className={cn('text-3xl font-bold text-gray-900 md:text-4xl')}>
                {resolvedTitleText}
              </Typography>
            </div>
          )}

          <div
            className={cn(
              'flex gap-0 md:gap-4xl items-center justify-start md:items-start',
              cleanImagePosition === 'hidden' || !block.image
                ? 'flex-col'
                : cleanImagePosition === 'left'
                  ? 'flex-col md:flex-row'
                  : cleanImagePosition === 'right'
                    ? 'flex-col md:flex-row-reverse'
                    : 'flex-col md:flex-row',
            )}
          >
            {block.image?.url && cleanImagePosition !== 'hidden' && (
              <div className="image-container md:w-91.75 md:h-91.75">
                <AppImage
                  src={sanityImageSrc(block.image, { width: 367, height: 367 })}
                  alt={block.image.bynderImage?.alt || block.image.alt || 'Resource hub image'}
                  width={367}
                  height={367}
                  className={cn(
                    'object-fill h-full shrink-0 hidden md:block',
                    cleanImagePosition === 'hidden' || !block.image
                      ? 'hidden'
                      : 'max-w-91.75! w-91.75 max-h-91.75! h-91.75!',
                  )}
                  // sizes="367"
                  priority={false}
                />
              </div>
            )}

            <div
              className={cn(
                cleanImagePosition === 'hidden' || !block.image
                  ? 'w-full'
                  : 'md:flex-1 w-full max-w-[509px] md:min-h-91.75 items-center flex',
              )}
            >
              {hasVisibleSections ? (
                <Accordion items={transformedAccordionItems} defaultExpanded={defaultExpandedIds} />
              ) : (
                <div className="text-center text-secondary-50 py-8">
                  <p>No document sections available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
